'use client';

import React, { useState } from 'react';
import FilterSidebar from './FilterSidebar';
import ResultsHeader from './ResultsHeader';
import FlightResultsList from './FlightResultsList';
import HotelResultsList from './HotelResultsList';
import PriceHistogram from './PriceHistogram';
import ResultsSkeleton from './ResultsSkeleton';
import CurrenciesTab from './CurrenciesTab';
import CurrencySelector from './CurrencySelector';
import Icon from '@/components/ui/AppIcon';
import { CurrencyCode } from '@/lib/travelpayouts';

export default function SearchResultsContent() {
  const [activeTab, setActiveTab] = useState<'flights' | 'hotels' | 'packages' | 'currencies'>('flights');
  const [loading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sortBy, setSortBy] = useState('price-asc');
  const [currency, setCurrency] = useState<CurrencyCode>('USD');
  const [filters, setFilters] = useState({
    maxPrice: 1200,
    stops: [] as string[],
    airlines: [] as string[],
    departureTime: [] as string[],
    maxDuration: 24,
    minRating: 0,
    amenities: [] as string[],
    dealTypes: [] as string[],
  });

  const tabs = [
    { id: 'flights', label: 'Flights', icon: 'PaperAirplaneIcon', count: 248 },
    { id: 'hotels', label: 'Hotels', icon: 'BuildingOffice2Icon', count: 92 },
    { id: 'packages', label: 'Packages', icon: 'GiftIcon', count: 34 },
    { id: 'currencies', label: 'Currencies', icon: 'CurrencyDollarIcon', count: null },
  ] as const;

  return (
    <div className="min-h-screen bg-background">
      {/* Results Topbar */}
      <div className="bg-card border-b border-border sticky top-16 z-30">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16">
          {/* Search Summary */}
          <div className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">New York → London</span>
              <Icon name="ChevronRightIcon" size={14} />
              <span>Jun 14 — Jun 21 · 2 Adults · Economy</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Currency Selector (quick access) */}
              <CurrencySelector currency={currency} onChange={setCurrency} />
              <button
                className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1.5"
                onClick={() => window.history.back()}
              >
                <Icon name="PencilIcon" size={14} />
                Modify Search
              </button>
            </div>
          </div>

          {/* Tab Bar */}
          <div className="flex items-center gap-1 border-t border-border pt-2 pb-0">
            {tabs.map((tab) => (
              <button
                key={`results-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition-all duration-150 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                {tab.label}
                {tab.count !== null && (
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      activeTab === tab.id ? 'bg-secondary text-primary' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">
        {/* Currencies Tab — full width, no sidebar */}
        {activeTab === 'currencies' ? (
          <CurrenciesTab
            selectedCurrency={currency}
            onSelect={(c) => {
              setCurrency(c);
              setActiveTab('flights');
            }}
          />
        ) : (
          <div className="flex gap-6">
            {/* Filter Sidebar — Desktop */}
            <aside className="hidden lg:block w-64 xl:w-72 flex-shrink-0">
              <FilterSidebar
                filters={filters}
                setFilters={setFilters}
                activeTab={activeTab as 'flights' | 'hotels' | 'packages'}
              />
            </aside>

            {/* Results Area */}
            <div className="flex-1 min-w-0">
              {/* Mobile Filter Button */}
              <div className="lg:hidden mb-4">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="btn-secondary px-4 py-2.5 text-sm flex items-center gap-2"
                >
                  <Icon name="AdjustmentsHorizontalIcon" size={16} />
                  Filters & Sort
                </button>
              </div>

              {/* Travelpayouts API Banner */}
              <div className="flex items-center gap-2 bg-secondary/60 border border-primary/20 rounded-xl px-4 py-2.5 mb-4 text-xs text-muted-foreground">
                <Icon name="BoltIcon" size={14} className="text-primary flex-shrink-0" />
                <span>
                  Prices powered by{' '}
                  <span className="font-semibold text-foreground">Travelpayouts</span> — live data from 1,200+ travel partners.
                </span>
                <span className="ml-auto font-semibold text-primary">
                  Showing in {currency}
                </span>
              </div>

              {loading ? (
                <ResultsSkeleton />
              ) : (
                <>
                  <PriceHistogram activeTab={activeTab as 'flights' | 'hotels' | 'packages'} />
                  <ResultsHeader
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    activeTab={activeTab as 'flights' | 'hotels' | 'packages'}
                    resultCount={activeTab === 'flights' ? 248 : activeTab === 'hotels' ? 92 : 34}
                  />
                  {activeTab === 'flights' && <FlightResultsList sortBy={sortBy} currency={currency} />}
                  {activeTab === 'hotels' && <HotelResultsList sortBy={sortBy} currency={currency} />}
                  {activeTab === 'packages' && (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mb-4">
                        <Icon name="GiftIcon" size={28} className="text-primary" />
                      </div>
                      <h3 className="text-lg font-700 text-foreground mb-2">No packages yet</h3>
                      <p className="text-sm text-muted-foreground max-w-xs">
                        Vacation package results for this route are loading. Try searching for flights or hotels in the meantime.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Filter Drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">
          <div
            className="mobile-menu-overlay absolute inset-0 bg-foreground/40"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="mobile-menu-drawer absolute left-0 top-0 bottom-0 w-80 bg-card overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card z-10">
              <h3 className="font-700 text-foreground">Filters</h3>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>
            <div className="p-4">
              <FilterSidebar
                filters={filters}
                setFilters={setFilters}
                activeTab={activeTab as 'flights' | 'hotels' | 'packages'}
              />
            </div>
            <div className="sticky bottom-0 bg-card border-t border-border p-4">
              <button
                onClick={() => setSidebarOpen(false)}
                className="btn-primary w-full py-3 text-sm"
              >
                Show Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}